import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, QueryList, Renderer2, SimpleChanges, ViewChild, ViewChildren, forwardRef } from '@angular/core';
import { WInputBase } from '@wipo/w-angular/shared';
import { CreateControlAccessProvider, filterDeep, WOption, WOptionEvent, WOptionOrString } from '@wipo/w-angular/shared';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
    selector: 'w-nested-checkbox',
    templateUrl: './w-nested-checkbox.component.html',
    providers: [CreateControlAccessProvider(WNestedCheckboxComponent)],
    
    host: {
        'class': 'b-nested-checkbox',
    },
    standalone: false
})
export class WNestedCheckboxComponent extends WInputBase implements OnChanges {
  @ViewChildren(forwardRef(() => WNestedCheckboxComponent)) children!: QueryList<WNestedCheckboxComponent>;
  @ViewChild('space') space!: ElementRef<HTMLSpanElement>; // if width=0 uses ellipsis - show title

  @Input() isLarge: boolean = false; // use large checkboxes
  @Input() canCollapse: boolean = false;
  @Input() isNested: boolean = false;
  @Input() option: WOption | string = '';

  @Input() filterText: string = ''; // filter text
  @Input() allValues?: any[]; // array with ALL selected values (all parent tree)
  @Input() showLimit: number = 0; // show ONLY n values; 0 => no limit
  @Input() showLimitAll: string = 'Show all';  // default message, provide customised or translation
  @Input() showLimitLess: string = 'Show less'; // default message, provide customised or translation
  showAll: boolean = false; // toggled by showAll/Less
  showCount = 0; // number of options after limit AND filter

  get showEmbedded(): boolean {
    return (!!this.isExpanded || !this.canCollapse) && !!this.embedded;
  }
  override get disabled() {
    return this.getProperty('disabled') || (typeof this.option !== 'string' && this.option?.disabled);
  }
  override set disabled(value: boolean) {
    this.setProperty('disabled', value);
  }
  @Output() wChange = new EventEmitter<WOptionEvent>();
  get showLimitMessage() {
    // if no limit or limit larger that number of options
    const embeddedLength = this.embedded?.length ?? 0;
    if (!this.showLimit || embeddedLength <= this.showCount) { return ''; }
    // else decide to use all or less
    return (embeddedLength > this.showCount && !this.showAll) ? this.showLimitAll : this.showLimitLess;
  }

  isEllipsis: boolean = false;
  spaceWidth = 1;

  l2 = `${Math.floor(Math.random() * 500)}`;

  get height(): number {
    const childrenHeight = this.children.reduce((h, child) => {
      return h + child.height;
    }, 0);
    const style = window.getComputedStyle(this._elementRef.nativeElement);
    const height =
      (style.marginTop ? parseInt(style.marginTop, 10) : 0) +
      (style.marginBottom ? parseInt(style.marginBottom, 10) : 0) +
      this._elementRef.nativeElement.getBoundingClientRect().height + childrenHeight;
    return height;
  }
  get checked(): boolean {
    const v = this.allValues;
    return Array.isArray(v) ? (this.isNode && this.children?.length ? this.treeChecked : v.includes(this.getValue(this.option))) : false;
  }
  get embedded() {
    return (typeof this.option !== 'string') && Array.isArray(this.option.nestedOptions) ? this.option.nestedOptions : undefined;
  }
  get isExpanded() {
    return typeof this.option !== 'string' && this.isNode && !!this.option.expanded;
  }
  get treeChecked() {
    return this.children.reduce((c, child) => {
      return c && child.checked;
    }, true);
  }
  get isNode() {
    return (typeof this.option !== 'string') ? Array.isArray(this.option.nestedOptions) : false;
  }

  // tslint:disable-next-line: variable-name
  constructor(protected _renderer: Renderer2, protected _elementRef: ElementRef) { super(); }
  ngOnChanges(changes: SimpleChanges): void {
    this.calculateShownItems();
  }

  // we need info about ellipsis only to show tooltip, trigger re-calc here
  @HostListener('mouseover')
  checkEllipsis() {
    const spaceWidth = this.space?.nativeElement?.clientWidth;
    this.isEllipsis = spaceWidth === 0;
  }

  getTitle(option: WOption | string) {
    if (typeof option === 'string') {
      return of(this.isEllipsis ? option : undefined);
    } else {
      const o = option as WOption;
      const asy = this.getLabelAsync(option);
      return this.isEllipsis ? asy.pipe(map((text: string) => text + (option?.tip ? `, ${option?.tip}` : ''))) : of(o?.tip);
    }
  }
  getSelected(): WOptionOrString | WOptionOrString[] {
    return this.isNode ? this.children.reduce((arr, child) => {
      const selected = child.getSelected();
      if (selected) {
        if (Array.isArray(selected)) {
          arr.splice(arr.length, 0, ...selected);
        } else {
          arr.push(selected);
        }
      }
      return arr;
    }, [] as WOptionOrString[]) : (this.checked ? this.option : []);
  }

  showOptionByIndex(option: WOptionOrString, i: number) {
    return (this.showAll || this.showCount === 0 || this.showCount > i) &&
      (!this.filterText || this.showFilteredOption(option));
  }
  showFilteredOption(option: WOptionOrString) {
    return !!this.filterText && filterDeep(option, this.filterText.toLowerCase());
  }
  // recalculate on any input changes or filterText change
  calculateShownItems(): void {
    if (!(this.isExpanded || !this.canCollapse) || this.showAll || this.showLimit === 0 || typeof this.option === 'string' ||
      !Array.isArray(this.option?.nestedOptions)) {
      this.showCount = 0; // no limit
    } else if (!this.filterText) {
      this.showCount = this.showLimit; // limit, but no no filtering
    } else if (this.option) {
      this.showCount = + this.option?.nestedOptions.reduce((count, o, i) => {
        return count + (i < count && !filterDeep(o, this.filterText.toLowerCase()) ? 1 : 0);
      }, this.showLimit);
    }
  }
  toggleAll() {
    this.showAll = !this.showAll;
  }

  toggle(e: Event) {
    if (!this.disabled && typeof this.option !== 'string') {
      this.option.expanded = !this.option.expanded;
    }
    e.stopPropagation();
  }
  toggleValue(event: Event) {
    if (!this.disabled) {
      this.select(!this.checked);
      this.wChange.emit(this.getSelection(this.option, event));
      event.stopPropagation();
    }
  }

  select(checked: boolean, defaultValues = [] as any[]) {
    // init if necessary
    this.allValues = this.allValues || defaultValues;
    if (!this.isNode) {
      const value = this.getValue(this.option);
      const ptr = this.allValues.indexOf(value);
      if (checked && ptr === -1) {
        // add
        this.allValues.push(value);
      } else if (!checked && ptr > -1) {
        // remove
        this.allValues.splice(ptr, 1);
      }
    } else {
      // node, update values from children
      this.children?.forEach(c => c.select(checked, this.allValues));
    }
  }

  onChildChange(event: WOptionEvent) {
    const selected = this.getSelected();
    this.wChange.emit({ ...event, selected });
  }
  getSelection(source: WOption | string, event: Event): WOptionEvent {
    const selected = this.getSelected();
    return { event, source, value: this.allValues, selected };
  }
}
